import {GeneralUtility} from './utilities/general-utility';
import {Modal} from 'bootstrap';
import {LanguageUtility} from './utilities/language-utility';
import {DataTable} from 'simple-datatables';
import {SimpleDatatablesUtility} from './utilities/simple-datatables-utility';
import {DataTableOptions} from 'simple-datatables/dist/dts/types';
import moment from 'moment';
import {AbstractContent} from './components/abstract-content';
import {QrCheckResult} from './types/qr-check-result';
import QrScanner from "qr-scanner";

/**
 * Check QR-Code action of the event plugin. Scans a QR-Code and sends it to a TYPO3 middleware for checking.
 */
export class ScanQrCode extends AbstractContent<null> {
  // UID of the event to scan QR-Codes for
  private eventUid: number;

  // Video element used to scan QR-Codes
  private readonly video: HTMLVideoElement;

  // Button to start a manual QR-Code scan
  private manualScanButton: HTMLButtonElement;

  // Input field for manual QR-Code scanning
  private manualQrCodeField: HTMLInputElement;

  // Contains the bootstrap modal for showing status information
  private modalContainer: HTMLElement;

  // Modal title
  private modalTitle: HTMLElement;

  // Modal content
  private modalText: HTMLElement;

  // Modal footer
  private modalFooter: HTMLElement;

  // Button to dismiss the modal
  private modalDismissButton: HTMLElement;

  // Bootstrap modal instance
  private modal: Modal;

  // QR-Scanner
  private qrScanner: QrScanner;

  // If true, no further attempts to scan a QR-Code will be allowed
  private stopScanning: boolean = false;

  // Timeout in milliseconds, after scanning will be allowed again
  private stopScanningTimeout: number = 10000;

  // Button for reloading the table
  private reloadButton: HTMLButtonElement;

  // Container for all elements of the registrations DataTable
  private registrationsWrapper: HTMLTableElement;

  // DataTable table for the registrations
  private registrationsTable: HTMLTableElement;

  // DataTable instance for the registrations table
  private registrationsDataTable: DataTable | null = null;

  constructor(container: HTMLElement) {
    super(container);
    if (!container.dataset.eventUid || isNaN(parseInt(container.dataset.eventUid))) {
      throw new Error('Event UID missing.');
    }
    this.eventUid = parseInt(container.dataset.eventUid);
    const video = this.container.querySelector<HTMLVideoElement>('video');
    const reloadButton = this.container.querySelector<HTMLButtonElement>('#reload');
    const registrationsWrapper = this.container.querySelector<HTMLTableElement>('.registrations-wrapper');
    const registrationsTable = this.container.querySelector<HTMLTableElement>('#registrations-table');
    const manualScanButton = this.container.querySelector<HTMLButtonElement>('#manual-scan');
    const manualQrCodeField = this.container.querySelector<HTMLInputElement>('#manual-qr-code');
    const modalContainer = this.createModalContainer();
    document.body.append(modalContainer);

    // Video Element will be missing, if no event to scan was selected
    if (
      !video ||
      !reloadButton ||
      !registrationsWrapper ||
      !registrationsTable ||
      !manualScanButton ||
      !manualQrCodeField ||
      !modalContainer
    ) {
      throw new Error('Necessary elements missing.');
    }
    this.video = video;
    this.reloadButton = reloadButton;
    this.registrationsWrapper = registrationsWrapper;
    this.registrationsTable = registrationsTable;
    this.registrationsDataTable = null;
    this.manualScanButton = manualScanButton;
    this.manualQrCodeField = manualQrCodeField;
    this.modalContainer = modalContainer;

    const modal = Modal.getOrCreateInstance(modalContainer as Element);
    const modalTitle = modalContainer.querySelector<HTMLElement>('.modal-title');
    const modalText = modalContainer.querySelector<HTMLElement>('.modal-text');
    const modalFooter = modalContainer.querySelector<HTMLElement>('.modal-footer');
    const modalDismissButton = modalContainer.querySelector<HTMLElement>('[data-bs-dismiss="modal"]');
    if (!modal || !modalTitle || !modalText || !modalFooter || !modalDismissButton) {
      throw new Error('Necessary elements missing.');
    }
    this.modal = modal;
    this.modalTitle = modalTitle;
    this.modalText = modalText;
    this.modalFooter = modalFooter;
    this.modalDismissButton = modalDismissButton;

    // The qr-scanner package will continuously call the result method, when a QR-Code was scanned successfully, also if
    // the data did not change. Therefore, it is necessary, to implement some checking of the last scanned data and a
    // timeout, to allow to scan previously scanned QR-Codes again.
    this.qrScanner = new QrScanner(
      this.video,
      result => {
        if (!this.stopScanning) {
          this.checkIn(result.data);
          setTimeout(() => {
            this.stopScanning = false;
          }, this.stopScanningTimeout);
        }
        this.stopScanning = true;
      },
      {
        maxScansPerSecond: 1,
        highlightScanRegion: true,
        highlightCodeOutline: true
      }
    );

    // Start the QR-Scanner
    this.startScanner();

    this.modalContainer.addEventListener('hidden.bs.modal', () => {
      // When the modal with status messages is closed, scanning can start again
      this.startScanner();

      // Some modals may create their own custom elements, which need to be removed every time the modal closes again
      this.modalText.querySelector<HTMLSelectElement>('#amount')?.remove();
      this.modalFooter.querySelector<HTMLButtonElement>('#check-in')?.remove();
    });

    // Initialize manual check in of QR-Codes
    this.initializeManualCheckIn();

    // Initialize the list of registrations
    this.updateRegistrations();

    // Reload table on demand
    this.reloadButton.addEventListener('click', () => {
      this.updateRegistrations();
    });

    // Listen to some global events, emitted by e.g. custom links or buttons within the table
    this.registrationsTable.addEventListener('click', (e: Event) => {
      const target = (e.target as HTMLElement).closest<HTMLElement>('.check-in[data-qr-code]');
      if (target) {
        const qrCode = target.dataset.qrCode;
        if (qrCode) {
          this.checkIn(qrCode);
        }
      }
    });

    window.emitter.on('plugin.scanQrCode.checkInFromCell', (qrCode: string) => {
      if (qrCode) {
        this.checkIn(qrCode);
      }
    });

    this.emitter.emit('ready', this);
  }

  /**
   * Initializes the button for manually checking in a registration by its QR-Code.
   */
  private initializeManualCheckIn(): void {
    this.manualScanButton.addEventListener('click', () => {
      const qrCode = this.manualQrCodeField.value;
      if (!qrCode) {
        return;
      }
      this.checkIn(qrCode);
      this.manualQrCodeField.value = '';
    });
  }

  /**
   * Creates a general modal container.
   */
  private createModalContainer(): HTMLElement {
    const html = `
<div class="modal-dialog modal-dialog-centered">
    <div class="modal-content">
        <div class="modal-header">
            <h5 class="modal-title"></h5>
        </div>
        <div class="modal-body">
            <p class="modal-text my-2"></p>
        </div>
        <div class="modal-footer">
            <button
                    type="button"
                    class="btn btn-secondary"
                    data-bs-dismiss="modal">
                ${LanguageUtility.translate('close')}
            </button>
        </div>
    </div>
</div>
        `;

    const element = document.createElement('div');
    element.id = 'scan-qr-code-modal';
    element.classList.add('modal');
    element.tabIndex = -1;
    element.innerHTML = html;
    return element;
  }

  /**
   * Starts the scanner.
   */
  private startScanner(): void {
    this.qrScanner.start();
    this.video.classList.remove('d-none');
    this.hideScannerSpinner();
  }

  /**
   * Pauses the scanner.
   */
  private pauseScanner(): void {
    this.qrScanner.pause();
    this.video.classList.add('d-none');
    this.showScannerSpinner();
  }

  /**
   * Shows the scanner-spinner.
   */
  private showScannerSpinner(): void {
    this.container.classList.add('show-scanner-spinner');
  }

  /**
   * Hides the scanner-spinner.
   */
  private hideScannerSpinner(): void {
    this.container.classList.remove('show-scanner-spinner');
  }

  /**
   * Checks in a specific registration.
   * @param {string} qrCode QR-Code of the registration to check in
   * @param {number|null} [amount] Amount of registrations to check in
   * @private
   */
  private checkIn(qrCode: string, amount: number | null = null): void {
    this.pauseScanner();
    fetch(`${GeneralUtility.getUrl()}/?eventApi[endpoint][]=checkInRegistration&eventApi[qrCode]=${qrCode}&eventApi[amount]=${amount}`)
      .then(response => {
        return response.json();
      })
      .then((result: QrCheckResult) => {
        if (result.status === 0) {
          this.checkInSuccess(result);
          this.updateRegistrations();
        } else if (result.status === 1) {
          this.checkInWarning(result);
        } else if (result.status === 2) {
          this.checkInMultiple(result);
        } else {
          this.checkInError(result);
        }
      })
      .catch(e => {
        this.error(e);
      });
  }

  /**
   * Shows a modal when a check in was successful.
   *
   * @param {QrCheckResult} result
   */
  private checkInSuccess(result: QrCheckResult): void {
    this.setModalSeverity('success');
    this.modalTitle.innerHTML = '✅ ' + result.reason;
    this.modalText.innerHTML = result.description;
    this.modalDismissButton.textContent = LanguageUtility.translate('ok');
    this.modal.show();
  }

  /**
   * Shows a warning-modal when a check in was unsuccessful.
   *
   * @param {QrCheckResult} result
   */
  private checkInWarning(result: QrCheckResult): void {
    this.setModalSeverity('warning');
    this.modalTitle.innerHTML = '⚠️' + result.reason;
    this.modalText.innerHTML = result.description;
    this.modalDismissButton.textContent = LanguageUtility.translate('ok');
    this.modal.show();
  }

  /**
   * Shows an eror-modal when a check in was unsuccessful.
   *
   * @param {QrCheckResult} result
   */
  private checkInError(result: QrCheckResult): void {
    this.setModalSeverity('error');
    this.modalTitle.innerHTML = '❌ ' + result.reason;
    this.modalText.innerHTML = result.description;
    this.modalDismissButton.textContent = LanguageUtility.translate('ok');
    this.modal.show();
  }

  /**
   * Shows a modal that allows selecting how many registrations to check in at once.
   *
   * @param {QrCheckResult} result
   */
  private checkInMultiple(result: QrCheckResult): void {
    this.setModalSeverity('multiple');
    this.modalTitle.innerHTML = 'ℹ️' + result.reason;
    this.modalText.innerHTML = result.description;
    this.modalDismissButton.textContent = LanguageUtility.translate('cancel');

    let amountOptions = '';
    for (let i = 0; i < result.data.count - result.data.checkedInCount; i++) {
      amountOptions += `<option value="${i + 1}">${i + 1}</option>`;
    }

    this.modalText.insertAdjacentHTML('beforeend', `
<div class="form-group">
  <select id="amount" class="form-control custom-select force-value">${amountOptions}</select>
  <label for="amount" class="form-label">${LanguageUtility.translate('plugin.scanQrCode.selectAmountToCheckIn')}</label>
</div>
`);

    this.modalFooter.insertAdjacentHTML('beforeend', `
<button id="check-in" type="button" class="btn btn-primary">
  ${LanguageUtility.translate('plugin.scanQrCode.checkInNow')}
</button>
`);

    const amountSelect = this.modalText.querySelector<HTMLSelectElement>('#amount');
    const checkInButton = this.modalFooter.querySelector<HTMLButtonElement>('#check-in');
    if (!amountSelect || !checkInButton) {
      throw new Error('Required elements missing.');
    }

    this.modal.show();
    checkInButton.addEventListener('click', () => {
      const amount = parseInt(amountSelect.value);
      if (isNaN(amount)) {
        throw new Error('Aamount value invalid.');
      }

      this.modal.hide();
      this.checkIn(result.data.qrCode, amount);
    });
  }

  /**
   * Shows a general error modal.
   *
   * @param {string} error
   */
  private error(error?: string): void {
    this.modalTitle.classList.remove('text-success');
    this.modalTitle.classList.add('text-danger');
    this.modalTitle.textContent = '❌ ' + LanguageUtility.translate('plugin.scanQrCode.error.title');

    if (error) {
      this.modalText.innerHTML = `
${LanguageUtility.translate('plugin.scanQrCode.error.description')}<br>
<span class="font-monospace text-danger">${error}</span>
`;
    } else {
      this.modalText.innerHTML = LanguageUtility.translate('plugin.scanQrCode.error.description');
    }

    this.modalDismissButton.classList.remove('btn-success');
    this.modalDismissButton.classList.add('btn-danger');
    this.modalDismissButton.textContent = LanguageUtility.translate('ok');
    this.modal.show();
  }

  /**
   * Sets the severity of the currently shown modal.
   *
   * @param {string} severity
   */
  private setModalSeverity(severity: string): void {
    this.modalTitle.classList.remove(...this.modalTitle.classList);
    this.modalDismissButton.classList.remove(...this.modalDismissButton.classList);

    let titleClasses = ['text-success'];
    let dismissButtonClasses = ['btn', 'btn-success'];
    switch (severity) {
      case 'warning':
        titleClasses = ['text-body'];
        dismissButtonClasses = ['btn', 'btn-warning'];
        break;
      case 'error':
        titleClasses = ['text-danger'];
        dismissButtonClasses = ['btn', 'btn-danger'];
        break;
      case 'multiple':
        titleClasses = ['text-body'];
        dismissButtonClasses = ['btn', 'btn-secondary'];
        break;
    }

    this.modalTitle.classList.add(...titleClasses);
    this.modalDismissButton.classList.add(...dismissButtonClasses);
  }

  /**
   * Updates the table of registrations.
   */
  private updateRegistrations(): void {
    // Do not show spinner at first initialization
    if (this.registrationsDataTable !== null) {
      this.showRegistrationsSpinner();
    }

    fetch(`${GeneralUtility.getUrl()}/?eventApi[endpoint][]=registration&eventApi[event]=${this.eventUid}`)
      .then(response => {
        return response.json();
      })
      .then(data => {
        const options: DataTableOptions = {
          columns: [
            {
              select: 2,
              type: 'date',
              format: 'ISO_8601',
              render: (data: any, td: any, rowIndex: any, cellIndex: any) => {
                if (data === null) {
                  return '-';
                }
                const dateTime = moment(data.toString());
                if (dateTime.isValid()) {
                  return dateTime.format('DD.MM.YYYY, HH:mm');
                }
                return '';
              }
            },
            {
              select: 3,
              type: 'other',
              sortable: false,
              render: (data: any) => {
                if (data.length > 0) {
                  const dateTimes = [];
                  for (const date of data) {
                    const dateTime = moment(date.toString());
                    if (dateTime.isValid()) {
                      dateTimes.push(dateTime.format('DD.MM.YYYY, HH:mm'));
                    }
                  }
                  return dateTimes.length > 0 ? dateTimes.join('<br/>') : '';
                }
                return '-';
              }
            },
            {
              select: 4,
              type: 'other',
              render: (data: any) => {
                const progress = (data.checkedInCount / data.count) * 100;
                if (data.checkedInCount === 0) {
                  return `<div class="progress" role="progressbar" aria-valuenow="${data.checkedInCount}" aria-valuemin="0" aria-valuemax="${data.count}"><div class="progress-zero">0/${data.count}</div></div>`;
                } else {
                  return `<div class="progress" role="progressbar" aria-valuenow="${data.checkedInCount}" aria-valuemin="0" aria-valuemax="${data.count}"><div class="progress-bar" style="width: ${progress}%">${data.checkedInCount}/${data.count}</div></div>`;
                }
              }
            },
            {
              select: 5,
              type: 'string',
              render: (data: any, td: any) => {
                if (!data) {
                  return LanguageUtility.translate('plugin.scanQrCode.allCheckedIn');
                }
                return `<button type="button" class="check-in btn btn-sm btn-primary" data-qr-code="${data}" title="${LanguageUtility.translate('plugin.scanQrCode.checkInNow')}">${LanguageUtility.translate('plugin.scanQrCode.checkIn')}</button>`;
              }
            },
            {
              select: 7,
              hidden: true
            }
          ]
        };

        // Setting the property dataTable.data to a new data object and then calling dataTable.update() does not work as
        // expected. DataTable expects a different type of data object at this point, instead of the "raw" data object
        // we have here. Therefore, remove all rows and re-add them, to update the DataTable instance.
        if (
          this.registrationsDataTable &&
          data.hasOwnProperty('data') &&
          data.hasOwnProperty('headings')
        ) {
          const rowIdsToRemove = [];
          for (let i = 0; i < this.registrationsDataTable.data.data.length; i++) {
            rowIdsToRemove.push(i);
          }
          this.registrationsDataTable.rows.remove(rowIdsToRemove);

          for (const row of data.data) {
            this.registrationsDataTable.rows.add(row);
          }

          if (this.registrationsDataTable.columns._state.sort) {
            this.registrationsDataTable.columns.sort(
              this.registrationsDataTable.columns._state.sort.column,
              this.registrationsDataTable.columns._state.sort.dir
            );
          }
        } else {
          this.registrationsDataTable = SimpleDatatablesUtility.createDatatableInstance(this.registrationsTable, data, options);
        }

        this.hideRegistrationsSpinner();
      })
      .catch(e => {
        this.error(e);
      });
  }

  /**
   * Shows the spinner when loading the registrations table.
   */
  private showRegistrationsSpinner(): void {
    this.container.classList.add('show-registrations-spinner');
  }

  /**
   * Hides the spinner when loading the registrations table.
   */
  private hideRegistrationsSpinner(): void {
    this.container.classList.remove('show-registrations-spinner');
  }
}
